/**
 * Enhanced OpenSolar SDK View
 *
 * This component wraps the OpenSolar SDK for easier integration.
 */
import { useEffect, useState } from 'react';
import { getSdkTokens } from '@/lib/opensolar-token-manager';
import { OpenSolarProject } from '@shared/opensolar-types';
import { useToast } from '@/hooks/use-toast';
import { useOpenSolarSdk } from '@/hooks/use-opensolar-sdk';
import { Button } from '@/components/ui/button';
// The ossdk-react library exports the main component as default export
import OsSdkView from 'ossdk-react';

// Define the expected ProjectData type from the SDK
interface ProjectData {
  identifier: string;
  [key: string]: any;
}

export interface EnhancedSdkViewProps {
  projectId?: string;
  designId?: string;
  projectData?: OpenSolarProject[];
  height?: number;
  className?: string;
  onDesignDataFetched?: (data: any) => void;
  autoFetchDesignData?: boolean;
}

export function EnhancedSdkView({
  projectId,
  designId,
  projectData = [],
  height = 700,
  className = '',
  onDesignDataFetched,
  autoFetchDesignData = false,
}: EnhancedSdkViewProps) {
  const [tokens, setTokens] = useState<{
    sdkKey?: string;
    token?: string;
    orgId?: string | number;
    accessToken?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { toast } = useToast();

  // Use our OpenSolar SDK hook
  const { handleSdkLoad, fetchDesignData, isIdle } = useOpenSolarSdk({
    onDesignDataFetched,
    onQueueProcessed: ({ idle }) => {
      // If the SDK is idle and we should auto-fetch design data, do it
      if (idle && autoFetchDesignData && onDesignDataFetched) {
        fetchDesignData();
      }
    },
  });

  // Fetch the tokens when the component mounts
  useEffect(() => {
    const fetchTokens = async () => {
      try {
        setLoading(true);
        const sdkTokens = await getSdkTokens();
        // Map the tokens to the expected format
        setTokens({
          sdkKey: sdkTokens.sdkKey || sdkTokens.accessToken,
          token: sdkTokens.token || sdkTokens.accessToken,
          orgId: sdkTokens.orgId,
          accessToken: sdkTokens.accessToken,
        });
        setError(null);
      } catch (err) {
        console.error('Error fetching SDK tokens:', err);
        setError('Failed to get OpenSolar tokens');
        toast({
          title: 'Authentication Error',
          description: 'Failed to get OpenSolar tokens',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTokens();
  }, [toast]);

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center ${className}`}
        style={{ height: `${height}px` }}
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !tokens) {
    return (
      <div
        className={`flex flex-col items-center justify-center ${className}`}
        style={{ height: `${height}px` }}
      >
        <div className="text-red-500 mb-4">{error || 'Failed to initialize OpenSolar SDK'}</div>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className={className} style={{ height: `${height}px` }}>
      <OsSdkView
        key={`project-${projectId || 'new'}`}
        osSdkKey={tokens.sdkKey || ''}
        osToken={tokens.token || ''}
        projectData={projectData.map((project) => ({
          identifier: project.identifier || String(project.id || ''),
          ...project,
        }))}
        height={700}
        onLoad={handleSdkLoad}
      />
    </div>
  );
}
